import { Field, FieldArray, Formik } from 'formik'
import type { FieldProps, FormikProps } from 'formik'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useDebounceValue } from 'usehooks-ts'

import { DEFAULT_LOCALE } from '../../questions/questionFormOptions'

import { useMutation, useQueryList } from '@percent/utility'
import {
  Accordion,
  AcknowledgeModal,
  Badge,
  ButtonText,
  EndlessAsyncSelect,
  Button,
  Flex,
  FormField,
  Heading,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  TextArea,
  TextInput
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { CreateFormFormValues, CreateFormModalProps, SectionFormValues } from './CreateFormModal.types'
import { HEX_COLOR_REGEX, QUESTION_TYPE_LABELS, getConditionalTargetIds, hasConditionalOptions } from './constants'
import { FormPreview } from './FormPreview'
import { QuestionPickerModal } from './QuestionPickerModal'
import modalStyles from './CreateFormModal.module.scss'

const generateUid = () => crypto.randomUUID()

const createEmptySection = (): SectionFormValues => ({
  uid: generateUid(),
  name: '',
  description: '',
  questions: []
})

export function CreateFormModal({ isOpen, onClose, refresh }: Readonly<CreateFormModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService, adminService } = useServices()
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)
  const [partnerSearch, setPartnerSearch] = useState('')
  const [debouncedPartnerSearch] = useDebounceValue(partnerSearch, 500)
  const [selectedPartnerId, setSelectedPartnerId] = useState('')
  const [questionPickerPageIndex, setQuestionPickerPageIndex] = useState<number | null>(null)

  const [{ data: partnerListData, isLoading: isPartnersLoading }, { nextPage: nextPartnerListPage }] = useQueryList(
    ['/v1/admin/partners', debouncedPartnerSearch],
    adminService.queries.getPartnerList,
    { query: debouncedPartnerSearch || undefined, pageSize: 20 }
  )

  const partnerNameOptions = useMemo(
    () => partnerListData?.map(p => ({ label: p.name, value: p.id })) ?? [],
    [partnerListData]
  )

  const hasMorePartners = !!nextPartnerListPage
  const loadMorePartners = useCallback(() => {
    if (!isPartnersLoading) {
      nextPartnerListPage?.()
    }
  }, [isPartnersLoading, nextPartnerListPage])

  const selectedPartnerName = useMemo(
    () => partnerNameOptions.find(p => p.value === selectedPartnerId)?.label ?? '',
    [partnerNameOptions, selectedPartnerId]
  )

  const [{ isPending }, { apiFunc: createForm }] = useMutation(
    grantProgramService.createForm,
    () => {
      refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const formikRef = useRef<FormikProps<CreateFormFormValues> | null>(null)

  const handleClose = useCallback(() => {
    formikRef.current?.resetForm()
    onClose()
    setModalState(null)
    setPartnerSearch('')
    setSelectedPartnerId('')
    setQuestionPickerPageIndex(null)
  }, [onClose])

  const initialValues = useMemo<CreateFormFormValues>(
    () => ({
      internalName: '',
      description: '',
      partnerId: '',
      logo: '',
      primaryColor: '',
      secondaryColor: '',
      pages: [createEmptySection()]
    }),
    []
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        internalName: Yup.string().min(1).max(255).required(t('errorMessage.required')),
        description: Yup.string().optional(),
        partnerId: Yup.string().optional(),
        logo: Yup.string().url(t('grants.forms.createModal.logo.invalid')).optional(),
        primaryColor: Yup.string().matches(HEX_COLOR_REGEX, t('grants.forms.createModal.color.invalid')).optional(),
        secondaryColor: Yup.string().matches(HEX_COLOR_REGEX, t('grants.forms.createModal.color.invalid')).optional(),
        pages: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string().min(1).required(t('errorMessage.required'))
            })
          )
          .min(1)
          .required()
      }),
    [t]
  )

  const handleSubmit = useCallback(
    (values: CreateFormFormValues) => {
      const payload = {
        internalName: values.internalName,
        ...(values.description ? { description: { [DEFAULT_LOCALE]: values.description } } : {}),
        ...(values.partnerId ? { partnerId: values.partnerId } : {}),
        ...(values.logo ? { logo: values.logo } : {}),
        ...(values.primaryColor ? { primaryColor: values.primaryColor } : {}),
        ...(values.secondaryColor ? { secondaryColor: values.secondaryColor } : {}),
        pages: values.pages.map(page => ({
          title: { [DEFAULT_LOCALE]: page.name },
          ...(page.description ? { description: { [DEFAULT_LOCALE]: page.description } } : {}),
          ...(page.questions.length > 0 ? { questions: page.questions.map(q => ({ id: q.id })) } : {})
        }))
      }

      createForm(payload)
    },
    [createForm]
  )

  if (modalState === 'success') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="positive"
          title={t('grants.forms.createModal.success.title')}
          description={t('grants.forms.createModal.success.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  if (modalState === 'error') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="negative"
          title={t('grants.forms.createModal.error.title')}
          description={t('grants.forms.createModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} width={1100} showModalOverflow={false}>
      <Formik<CreateFormFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {formik => {
          formikRef.current = formik

          return (
            <>
              <ModalHeader>
                <Heading level="h4">{t('grants.forms.createModal.title')}</Heading>
              </ModalHeader>
              <ModalContent maxHeight="60vh">
                <Flex gap="lg" className={modalStyles.contentLayout}>
                  <Flex direction="column" gap="lg" className={modalStyles.formColumn}>
                    <Heading level="h5">{t('grants.forms.createModal.details')}</Heading>

                    <Field name="internalName">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.name.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="create-form-name"
                            placeholder={t('grants.forms.createModal.name.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="description">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.description.label')}
                          necessity="optional"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextArea
                            data-testid="create-form-description"
                            placeholder={t('grants.forms.createModal.description.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                            className={modalStyles.descriptionTextarea}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="partnerId">
                      {({ form }: FieldProps) => (
                        <FormField label={t('grants.forms.createModal.partner.label')} necessity="optional">
                          <EndlessAsyncSelect
                            data-testid="create-form-partner"
                            name="partnerId"
                            options={partnerNameOptions}
                            setQuery={setPartnerSearch}
                            query={partnerSearch}
                            loading={isPartnersLoading}
                            onChange={selected => {
                              const id = selected?.value ?? ''
                              form.setFieldValue('partnerId', id)
                              setSelectedPartnerId(id)
                            }}
                            placeholder={t('grants.forms.createModal.partner.placeholder')}
                            onScrollEnd={loadMorePartners}
                            hasMore={hasMorePartners}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="logo">
                      {({ field, meta }: FieldProps<string>) => (
                        <FormField
                          label={t('grants.forms.createModal.logo.label')}
                          necessity="optional"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="create-form-logo"
                            placeholder={t('grants.forms.createModal.logo.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Flex gap="md">
                      <Flex flex={1}>
                        <Field name="primaryColor">
                          {({ field, meta }: FieldProps<string>) => (
                            <FormField
                              label={t('grants.forms.createModal.primaryColor.label')}
                              necessity="optional"
                              status={meta.touched && meta.error ? 'danger' : 'default'}
                              statusMessage={meta.touched && meta.error ? meta.error : undefined}
                            >
                              <TextInput
                                data-testid="create-form-primary-color"
                                placeholder="#FF0000"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                status={meta.touched && meta.error ? 'danger' : 'default'}
                              />
                            </FormField>
                          )}
                        </Field>
                      </Flex>
                      <Flex flex={1}>
                        <Field name="secondaryColor">
                          {({ field, meta }: FieldProps<string>) => (
                            <FormField
                              label={t('grants.forms.createModal.secondaryColor.label')}
                              necessity="optional"
                              status={meta.touched && meta.error ? 'danger' : 'default'}
                              statusMessage={meta.touched && meta.error ? meta.error : undefined}
                            >
                              <TextInput
                                data-testid="create-form-secondary-color"
                                placeholder="#0000FF"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                status={meta.touched && meta.error ? 'danger' : 'default'}
                              />
                            </FormField>
                          )}
                        </Field>
                      </Flex>
                    </Flex>

                    <FieldArray name="pages">
                      {({ push, remove }) => (
                        <Flex direction="column" gap="md">
                          <Flex justify="space-between" align="center">
                            <Heading level="h5">{t('grants.forms.createModal.content')}</Heading>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => push(createEmptySection())}
                              data-testid="add-section-button"
                            >
                              {t('grants.forms.createModal.addSection')}
                            </Button>
                          </Flex>

                          <Accordion multiple defaultValue={['section-0']}>
                            {formik.values.pages.map((page, pageIndex) => (
                              <Accordion.Item key={page.uid} value={`section-${pageIndex}`}>
                                <Accordion.Control>
                                  <Flex justify="space-between" align="center">
                                    <Text>
                                      {page.name ||
                                        t('grants.forms.createModal.section.defaultTitle', { number: pageIndex + 1 })}
                                    </Text>
                                    <Text size="small" color="grey500">
                                      {t('grants.forms.createModal.section.questionCount', {
                                        count: page.questions.length
                                      })}
                                    </Text>
                                  </Flex>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <Flex direction="column" gap="md">
                                    <Field name={`pages.${pageIndex}.name`}>
                                      {({ field, meta }: FieldProps<string>) => (
                                        <FormField
                                          label={t('grants.forms.createModal.section.name.label')}
                                          necessity="required"
                                          status={meta.touched && meta.error ? 'danger' : 'default'}
                                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                                        >
                                          <TextInput
                                            data-testid={`section-name-${pageIndex}`}
                                            placeholder={t('grants.forms.createModal.section.name.placeholder')}
                                            name={field.name}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            status={meta.touched && meta.error ? 'danger' : 'default'}
                                          />
                                        </FormField>
                                      )}
                                    </Field>

                                    <Field name={`pages.${pageIndex}.description`}>
                                      {({ field }: FieldProps<string>) => (
                                        <FormField
                                          label={t('grants.forms.createModal.section.description.label')}
                                          necessity="optional"
                                        >
                                          <TextArea
                                            data-testid={`section-description-${pageIndex}`}
                                            placeholder={t('grants.forms.createModal.section.description.placeholder')}
                                            name={field.name}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            className={modalStyles.sectionDescriptionTextarea}
                                          />
                                        </FormField>
                                      )}
                                    </Field>

                                    <FieldArray name={`pages.${pageIndex}.questions`}>
                                      {({ push: pushQuestion, remove: removeQuestion }) => {
                                        const conditionalTargetIds = getConditionalTargetIds(page.questions)

                                        return (
                                          <FormField
                                            label={t('grants.forms.createModal.section.questions.label')}
                                            necessity="optional"
                                          >
                                            <Flex direction="column" gap="sm" w="100%">
                                              {page.questions.map((question, qIndex) => {
                                                const isConditionalTarget = conditionalTargetIds.has(question.id)

                                                return (
                                                  <Flex
                                                    key={question.uid}
                                                    gap="sm"
                                                    align="center"
                                                    py={12}
                                                    px={16}
                                                    className={modalStyles.questionCard}
                                                    style={
                                                      isConditionalTarget
                                                        ? { marginLeft: 16, borderLeft: '3px solid #6366f1' }
                                                        : undefined
                                                    }
                                                  >
                                                    <Flex flex={1}>
                                                      <Text size="small">{question.label}</Text>
                                                    </Flex>
                                                    {question.hidden && (
                                                      <Badge variant="default">
                                                        {t('grants.forms.createModal.section.questions.hidden')}
                                                      </Badge>
                                                    )}
                                                    {isConditionalTarget && (
                                                      <Badge variant="warning">
                                                        {t('grants.forms.createModal.section.questions.conditional')}
                                                      </Badge>
                                                    )}
                                                    {hasConditionalOptions(question) && (
                                                      <Badge variant="informative">
                                                        {t('grants.forms.createModal.section.questions.hasConditions')}
                                                      </Badge>
                                                    )}
                                                    <Badge variant="default">
                                                      {QUESTION_TYPE_LABELS[question.type] ?? question.type}
                                                    </Badge>
                                                    <Badge variant={question.required ? 'informative' : 'default'}>
                                                      {question.required
                                                        ? t('grants.forms.createModal.section.questions.required')
                                                        : t('grants.forms.createModal.section.questions.optional')}
                                                    </Badge>
                                                    <ButtonText
                                                      onClick={() => removeQuestion(qIndex)}
                                                      data-testid={`remove-question-${pageIndex}-${qIndex}`}
                                                      className={modalStyles.removeButton}
                                                    >
                                                      <Icon name="bin" size={5} color="gray400" />
                                                    </ButtonText>
                                                  </Flex>
                                                )
                                              })}
                                              <Button
                                                variant="tertiary"
                                                size="small"
                                                onClick={() => setQuestionPickerPageIndex(pageIndex)}
                                                data-testid={`add-question-to-section-${pageIndex}`}
                                              >
                                                {t('grants.forms.createModal.section.questions.add')}
                                              </Button>
                                              <QuestionPickerModal
                                                isOpen={questionPickerPageIndex === pageIndex}
                                                onClose={() => setQuestionPickerPageIndex(null)}
                                                onSelect={question => {
                                                  pushQuestion({
                                                    uid: generateUid(),
                                                    ...question
                                                  })
                                                }}
                                                excludedQuestionIds={new Set(page.questions.map(q => q.id))}
                                                partnerId={selectedPartnerId}
                                                partnerName={selectedPartnerName}
                                              />
                                            </Flex>
                                          </FormField>
                                        )
                                      }}
                                    </FieldArray>

                                    {formik.values.pages.length > 1 && (
                                      <Flex justify="flex-end">
                                        <Button
                                          variant="reject"
                                          size="small"
                                          onClick={() => remove(pageIndex)}
                                          data-testid={`remove-section-${pageIndex}`}
                                        >
                                          {t('grants.forms.createModal.section.remove')}
                                        </Button>
                                      </Flex>
                                    )}
                                  </Flex>
                                </Accordion.Panel>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </Flex>
                      )}
                    </FieldArray>
                  </Flex>
                  <Flex direction="column" className={modalStyles.previewPanel} data-testid="form-preview-panel">
                    <FormPreview values={formik.values} />
                  </Flex>
                </Flex>
              </ModalContent>
              <ModalFooter>
                <Flex gap="md" justify="flex-end">
                  <Button variant="secondary" onClick={handleClose} data-testid="create-form-cancel-button">
                    {t('button.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={formik.submitForm}
                    loading={isPending}
                    disabled={!formik.isValid || !formik.dirty || isPending}
                    data-testid="create-form-submit-button"
                  >
                    {t('grants.forms.createModal.submit')}
                  </Button>
                </Flex>
              </ModalFooter>
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}
